import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Button
} from '@hiring-intelligence/ui';

export interface IdentityActionItem {
	label: string;
	icon?: ReactNode;
	onClick?: () => void;
	destructive?: boolean;
}

interface IdentityHeaderProps {
	backTo: string;
	backLabel: string;
	avatar?: ReactNode;
	title: string;
	titleBadges?: ReactNode;
	subtitle?: ReactNode;
	metaRow?: ReactNode;
	actions?: IdentityActionItem[];
}

/**
 * Left-rail identity block: back link · Actions · big avatar/name · subtitle · meta row.
 * Mirrors HubSpot's contact / company / deal record header.
 */
export function IdentityHeader({
	backTo,
	backLabel,
	avatar,
	title,
	titleBadges,
	subtitle,
	metaRow,
	actions
}: IdentityHeaderProps) {
	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<Link
					to={backTo}
					className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
				>
					<ChevronLeft className="h-3.5 w-3.5" /> {backLabel}
				</Link>
				{actions && actions.length > 0 && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary">
								Actions
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-48">
							{actions.map((a, i) => (
								<DropdownMenuItem
									key={i}
									onClick={a.onClick}
									className={a.destructive ? 'text-destructive focus:text-destructive' : ''}
								>
									{a.icon && <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">{a.icon}</span>}
									{a.label}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
			<div className="flex items-start gap-3">
				{avatar && <div className="shrink-0">{avatar}</div>}
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-xl font-semibold leading-tight text-foreground truncate">{title}</h1>
						{titleBadges}
					</div>
					{subtitle && <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>}
				</div>
			</div>
			{metaRow && <div className="text-sm text-primary">{metaRow}</div>}
		</div>
	);
}
